import React, { useEffect, useState } from 'react';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { clsx } from 'clsx';
import { getInitialSidebarOpen } from './sidebarState';
// import { Footer } from './Footer';

interface LayoutProps {
  children: React.ReactNode;
  showSidebar?: boolean;
  /** When true, main content has no padding (e.g. AI Tutor Live full-bleed). */
  noPadding?: boolean;
  /**
   * When true, the shell height follows the parent (h-full) instead of locking to the viewport (h-dvh).
   * Use when Layout is nested under extra UI (e.g. Communication Tutor stats strip) so the bottom
   * control bar is not clipped by overflow-hidden.
   */
  fillParent?: boolean;
  /** Keep scrolling in the main pane only (avoids a duplicate document/body scrollbar). */
  lockDocumentScroll?: boolean;
  /** Let route content grow with the document instead of an inner main-pane scroll trap. */
  allowPageScroll?: boolean;
}

export const Layout: React.FC<LayoutProps> = ({
  children,
  showSidebar = true,
  noPadding = false,
  fillParent = false,
  lockDocumentScroll = false,
  allowPageScroll = false,
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(getInitialSidebarOpen);

  useEffect(() => {
    if (!lockDocumentScroll) return;
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const prevHtml = html.style.overflowY;
    const prevBody = body.style.overflowY;
    const prevRoot = root?.style.overflowY ?? '';
    const prevRootHeight = root?.style.height ?? '';
    html.style.overflowY = 'hidden';
    body.style.overflowY = 'hidden';
    body.classList.add('app-shell-scroll-lock');
    if (root) {
      root.style.overflowY = 'hidden';
      root.style.height = '100%';
    }
    return () => {
      html.style.overflowY = prevHtml;
      body.style.overflowY = prevBody;
      body.classList.remove('app-shell-scroll-lock');
      if (root) {
        root.style.overflowY = prevRoot;
        root.style.height = prevRootHeight;
      }
    };
  }, [lockDocumentScroll]);

  return (
    <div
      className={clsx(
        'app-layout-shell flex flex-col bg-app-bg dark:bg-gray-950',
        allowPageScroll
          ? 'min-h-dvh'
          : clsx(
              'overflow-hidden',
              lockDocumentScroll
                ? 'h-full min-h-0'
                : fillParent
                  ? 'h-full min-h-0 max-h-dvh'
                  : 'h-dvh max-h-screen',
            ),
      )}
    >
      <header className="h-[4.25rem] sm:h-[4.5rem] shrink-0">
        <Header onMenuClick={showSidebar ? () => setSidebarOpen((prev) => !prev) : undefined} />
      </header>

      {sidebarOpen && showSidebar && (
        <div
          className="fixed inset-0 top-[4.25rem] sm:top-[4.5rem] z-30 bg-black/40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className={clsx('flex flex-1', !allowPageScroll && 'min-h-0 overflow-hidden')}>
        {showSidebar && (
          <aside
            className={clsx(
              'shrink-0 border-r border-app-primary/20 dark:border-app-primary/30 h-full min-h-0',
              'hidden md:block md:sticky md:top-[4.5rem] md:self-start',
              !allowPageScroll && 'md:h-[calc(100dvh-4.5rem)] md:max-h-[calc(100vh-4.5rem)]',
              'transition-[width] duration-200',
              sidebarOpen ? 'md:w-64' : 'md:w-0 md:overflow-hidden md:border-r-0'
            )}
          >
            <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
          </aside>
        )}

        <main
          className={clsx(
            'flex-1 min-w-0 bg-app-bg dark:bg-gray-950 text-app-text dark:text-gray-100',
            allowPageScroll ? 'overflow-visible' : 'min-h-0 overflow-hidden flex flex-col'
          )}
        >
          <div
            data-app-main-scroll
            className={clsx(
              'w-full',
              allowPageScroll
                ? 'overflow-visible'
                : lockDocumentScroll && fillParent
                  ? 'flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden overscroll-y-contain'
                  : clsx(
                      'flex-1 min-h-0 overflow-y-auto',
                      lockDocumentScroll && 'min-w-0 overflow-x-hidden overscroll-y-contain',
                    ),
              !noPadding && 'p-4 sm:p-6 lg:p-8'
            )}
          >
            {children}
          </div>
        </main>
      </div>

      {showSidebar && (
        <aside
          className={clsx(
            'fixed left-0 top-[4.25rem] sm:top-[4.5rem] bottom-0 z-40 w-64 border-r border-app-border dark:border-gray-700 bg-app-surface dark:bg-gray-900 md:hidden',
            'transform transition-transform duration-200',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          )}
        >
          <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        </aside>
      )}
      {/* <Footer /> */}
    </div>
  );
};